import { useState, useEffect } from "react";
import { 
  X, RefreshCw, Mail, Phone, MessageCircle, Link2, 
  Sparkles, Clock, CheckCircle, Search, User, ArrowLeft,
  Inbox, Brain, FileText, Send, History, XCircle, AlertTriangle
} from "lucide-react";
import EmailDetailModal from "./components/EmailDetailModal";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


export default function CommunicationsHub({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("emails");
  
  // Email data
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailStats, setEmailStats] = useState({ pending: 0, processed: 0, total: 0, identified: 0, pending_link: 0 });
  const [processingEmails, setProcessingEmails] = useState(false);
  
  // Échanges à rattacher
  const [pendingExchanges, setPendingExchanges] = useState([]);
  
  // Historique des rattachements
  const [linkHistory, setLinkHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Modal de rattachement
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [linkingExchange, setLinkingExchange] = useState(null);
  
  // Modal de rejet
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingExchange, setRejectingExchange] = useState(null);
  
  // Modal de détail email
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    
    // Email History
    try {
      const resp = await authFetch(`${API}/connectors/email/history?limit=100`);
      if (resp.ok) {
        const data = await resp.json();
        setEmailHistory(data.emails || []);
        setEmailStats(data.stats || {});
      }
    } catch (e) { console.error(e); }
    
    // Pending count
    try {
      const resp = await authFetch(`${API}/emails/pending-count`);
      if (resp.ok) {
        const data = await resp.json();
        setEmailStats(prev => ({ ...prev, ...data }));
      }
    } catch (e) { console.error(e); }
    
    // Échanges à rattacher
    try {
      const resp = await authFetch(`${API}/exchanges/pending-link`);
      if (resp.ok) {
        const data = await resp.json();
        setPendingExchanges(data.exchanges || []);
      }
    } catch (e) { console.error(e); }
    
    // Historique des rattachements
    try {
      const resp = await authFetch(`${API}/exchanges/link-history`);
      if (resp.ok) {
        const data = await resp.json();
        setLinkHistory(data.history || []);
      }
    } catch (e) { console.error(e); }
    
    setLoading(false);
  };

  const processEmails = async (limit = 10) => {
    setProcessingEmails(true);
    try {
      const resp = await authFetch(`${API}/emails/process-batch?limit=${limit}`, { method: 'POST' });
      if (resp.ok) {
        const result = await resp.json();
        alert(`✅ ${result.success}/${result.total} emails traités\n${result.identified} clients identifiés\n${result.pending_link} à rattacher`);
        loadData();
      }
    } catch (e) { 
      console.error(e);
      alert("Erreur lors du traitement");
    }
    setProcessingEmails(false);
  };

  const processSingleEmail = async (emailId) => {
    try {
      const resp = await authFetch(`${API}/emails/process/${emailId}`, { method: 'POST' });
      if (resp.ok) {
        const result = await resp.json();
        if (result.client_identified) {
          alert(`✅ Client identifié: ${result.client_match?.client_name || 'Inconnu'}`);
        } else {
          alert(`❓ Client non identifié. Ajouté aux échanges à rattacher.`);
        }
        loadData();
      }
    } catch (e) { 
      console.error(e);
    }
  };

  // Recherche automatique avec debounce
  useEffect(() => {
    if (!showLinkModal) return;
    
    const timer = setTimeout(async () => {
      if (clientSearchTerm.length >= 2) {
        try {
          const resp = await authFetch(`${API}/factures/clients/search?q=${encodeURIComponent(clientSearchTerm)}`);
          if (resp.ok) {
            const data = await resp.json();
            setClientSearchResults(data.clients || []);
          }
        } catch (e) { console.error(e); }
      } else {
        setClientSearchResults([]);
      }
    }, 300); // 300ms de délai
    
    return () => clearTimeout(timer);
  }, [clientSearchTerm, showLinkModal]);

  const searchClients = async (searchTerm) => {
    const term = searchTerm || clientSearchTerm;
    if (term.length < 2) {
      setClientSearchResults([]);
      return;
    }
    try {
      const resp = await authFetch(`${API}/factures/clients/search?q=${encodeURIComponent(term)}`);
      if (resp.ok) {
        const data = await resp.json();
        setClientSearchResults(data.clients || []);
      }
    } catch (e) { console.error(e); }
  };

  const linkExchangeToClient = async (client) => {
    if (!selectedExchange) return;
    
    setLinkingExchange(selectedExchange._id);
    try {
      const formData = new FormData();
      formData.append('client_id', client.client_id);
      formData.append('client_name', `${client.prenom} ${client.nom}`.trim());
      formData.append('save_email', 'true');
      
      const resp = await authFetch(`${API}/exchanges/${selectedExchange._id}/link`, {
        method: 'POST',
        body: formData
      });
      
      if (resp.ok) {
        alert(`✅ Échange rattaché à ${client.prenom} ${client.nom}`);
        setShowLinkModal(false);
        setSelectedExchange(null);
        setClientSearchTerm("");
        setClientSearchResults([]);
        loadData();
      } else {
        const error = await resp.json();
        alert(`❌ Erreur: ${error.detail || 'Échec du rattachement'}`);
      }
    } catch (e) {
      console.error(e);
      alert(`❌ Erreur: ${e.message}`);
    }
    setLinkingExchange(null);
  };

  const rejectExchange = async () => {
    if (!selectedExchange) return;
    
    setRejectingExchange(selectedExchange._id);
    try {
      const formData = new FormData();
      formData.append('reason', rejectReason || 'Non pertinent');
      formData.append('rejected_by', 'admin'); // TODO: utiliser l'utilisateur connecté
      
      const resp = await authFetch(`${API}/exchanges/${selectedExchange._id}/reject`, {
        method: 'POST',
        body: formData
      });
      
      if (resp.ok) {
        alert(`✅ Échange rejeté`);
        setShowRejectModal(false);
        setSelectedExchange(null);
        setRejectReason("");
        loadData();
      } else {
        const error = await resp.json();
        alert(`❌ Erreur: ${error.detail || 'Échec du rejet'}`);
      }
    } catch (e) {
      console.error(e);
      alert(`❌ Erreur: ${e.message}`);
    }
    setRejectingExchange(null);
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Centre de Communications IA</h1>
                <p className="text-sm text-slate-400">Traitement et enrichissement automatique des échanges</p>
              </div>
            </div>
          </div>
          <button
            onClick={loadData}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-800/50 border-b border-slate-700 px-6 py-3">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-slate-400">Total:</span>
            <span className="font-bold">{emailStats.total || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-slate-400">En attente:</span>
            <span className="font-bold text-amber-400">{emailStats.pending || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-400">Identifiés:</span>
            <span className="font-bold text-emerald-400">{emailStats.identified || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-orange-400" />
            <span className="text-sm text-slate-400">À rattacher:</span>
            <span className="font-bold text-orange-400">{pendingExchanges.length}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-6 py-4 border-b border-slate-700">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab("emails")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === "emails" 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Mail className="w-4 h-4" />
            Emails ({emailStats.total || 0})
          </button>
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === "pending" 
                ? 'bg-orange-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Link2 className="w-4 h-4" />
            À rattacher ({pendingExchanges.length})
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === "history" 
                ? 'bg-green-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <History className="w-4 h-4" />
            Historique ({linkHistory.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Tab: Emails */}
        {activeTab === "emails" && (
          <div className="space-y-4">
            {/* Action Bar */}
            <div className="flex items-center justify-between bg-slate-800/50 border border-slate-700 rounded-xl p-4">
              <div>
                <h3 className="font-semibold">Traitement IA des emails</h3>
                <p className="text-sm text-slate-400">{emailStats.pending || 0} emails en attente de traitement</p>
              </div>
              <button
                onClick={() => processEmails(20)}
                disabled={processingEmails || (emailStats.pending || 0) === 0}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingEmails ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Traiter avec IA
              </button>
            </div>

            {/* Email List */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-semibold">Historique des emails</h3>
              </div>
              <div className="divide-y divide-slate-700 max-h-[500px] overflow-y-auto">
                {emailHistory.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Inbox className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun email récupéré</p>
                  </div>
                ) : emailHistory.map((email, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => setSelectedEmail(email)}
                    className="p-4 hover:bg-slate-800/50 cursor-pointer border-l-2 border-transparent hover:border-blue-500 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        email.client_identified ? 'bg-emerald-500/20' : 
                        email.processed ? 'bg-orange-500/20' : 'bg-amber-500/20'
                      }`}>
                        {email.client_identified ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : email.processed ? (
                          <Link2 className="w-5 h-5 text-orange-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-amber-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-sm truncate">{email.subject || '(Sans sujet)'}</p>
                          {email.client_identified && (
                            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs">✓ Identifié</span>
                          )}
                          {email.processed && !email.client_identified && (
                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded text-xs">À rattacher</span>
                          )}
                          {!email.processed && (
                            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">En attente</span>
                          )}
                        </div>
                        <p className="text-sm text-blue-400">De: {email.from}</p>
                        {email.body_preview && (
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{email.body_preview}</p>
                        )}
                        {email.summary && (
                          <p className="text-xs text-purple-400 mt-2 italic">📝 {email.summary}</p>
                        )}
                        {email.client_match?.client_name && (
                          <p className="text-xs text-emerald-400 mt-1">👤 {email.client_match.client_name}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          {email.date ? new Date(email.date).toLocaleString('fr-FR') : ''}
                        </p>
                      </div>
                      {!email.processed && (
                        <button
                          onClick={(e) => { e.stopPropagation(); processSingleEmail(email.id); }}
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded text-xs font-medium flex items-center gap-1"
                        >
                          <Sparkles className="w-3 h-3" />
                          Traiter
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab: À rattacher */}
        {activeTab === "pending" && (
          <div className="space-y-4">
            {/* Info */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
              <p className="text-sm text-blue-400 font-medium mb-2">💡 Comment ça marche ?</p>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Ces échanges n'ont pas pu être rattachés automatiquement à un client</li>
                <li>• Cliquez sur "Rattacher" pour associer l'échange à un client</li>
                <li>• L'email sera enregistré pour une identification automatique future</li>
              </ul>
            </div>

            {/* Pending List */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700">
                <h3 className="font-semibold">Échanges en attente de rattachement ({pendingExchanges.length})</h3>
              </div>
              <div className="divide-y divide-slate-700 max-h-[500px] overflow-y-auto">
                {pendingExchanges.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">Aucun échange en attente</p>
                    <p className="text-sm mt-1">Tous les échanges ont été rattachés 🎉</p>
                  </div>
                ) : pendingExchanges.map((exchange, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-800/50">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        {exchange.type === 'email' ? (
                          <Mail className="w-6 h-6 text-orange-400" />
                        ) : exchange.type === 'sms' ? (
                          <MessageCircle className="w-6 h-6 text-green-400" />
                        ) : (
                          <Phone className="w-6 h-6 text-blue-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{exchange.subject || '(Sans objet)'}</p>
                        <p className="text-sm text-slate-400">De: {exchange.from}</p>
                        {exchange.summary && (
                          <p className="text-sm text-purple-400 mt-2 italic">📝 {exchange.summary}</p>
                        )}
                        {exchange.extracted_info && (
                          <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-2">
                            {exchange.extracted_info.nom && (
                              <span className="bg-slate-700 px-2 py-1 rounded">Nom: {exchange.extracted_info.nom}</span>
                            )}
                            {exchange.extracted_info.prenom && (
                              <span className="bg-slate-700 px-2 py-1 rounded">Prénom: {exchange.extracted_info.prenom}</span>
                            )}
                          </div>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          {exchange.created_at ? new Date(exchange.created_at).toLocaleString('fr-FR') : ''}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          data-testid={`rattacher-btn-${exchange._id}`}
                          onClick={() => {
                            setClientSearchTerm("");
                            setClientSearchResults([]);
                            setSelectedExchange(exchange);
                            setShowLinkModal(true);
                          }}
                          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          <Link2 className="w-4 h-4" />
                          Rattacher
                        </button>
                        <button
                          data-testid={`rejeter-btn-${exchange._id}`}
                          onClick={() => {
                            setSelectedExchange(exchange);
                            setRejectReason("");
                            setShowRejectModal(true);
                          }}
                          className="px-4 py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/50 rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Rejeter
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab: Historique des rattachements */}
        {activeTab === "history" && (
          <div className="space-y-4">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <History className="w-5 h-5 text-green-400" />
                    Historique des rattachements
                  </h3>
                  <p className="text-sm text-slate-400 mt-1">
                    {linkHistory.length} échange(s) rattaché(s)
                  </p>
                </div>
              </div>
              <div className="divide-y divide-slate-700 max-h-[600px] overflow-y-auto">
                {linkHistory.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">Aucun historique</p>
                    <p className="text-sm mt-1">Les rattachements apparaîtront ici</p>
                  </div>
                ) : linkHistory.map((item, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-800/50">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        item.auto_linked ? 'bg-purple-500/20' : 'bg-amber-500/20'
                      }`}>
                        {item.auto_linked ? (
                          <Brain className="w-6 h-6 text-purple-400" />
                        ) : (
                          <User className="w-6 h-6 text-amber-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium">{item.original_data?.subject || item.subject || '(Sans sujet)'}</p>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            item.auto_linked 
                              ? 'bg-purple-500/20 text-purple-400' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {item.auto_linked ? '🤖 SAN-IA' : `👤 ${item.linked_by || 'Manuel'}`}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                          De: {item.original_data?.from || item.from || 'Inconnu'}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Rattaché à: {item.client_name}
                          </span>
                        </div>
                        {item.summary && (
                          <p className="text-sm text-cyan-400 mt-2 italic truncate">📝 {item.summary}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          {item.timestamp ? new Date(item.timestamp).toLocaleString('fr-FR') : 
                           item.linked_at ? new Date(item.linked_at).toLocaleString('fr-FR') : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal de rattachement */}
      {showLinkModal && selectedExchange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-xl mx-4 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Link2 className="w-5 h-5 text-orange-400" />
                Rattacher à un client
              </h2>
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setSelectedExchange(null);
                }}
                className="p-2 hover:bg-slate-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Info échange */}
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-sm text-slate-400">Échange à rattacher :</p>
                <p className="font-medium mt-1">{selectedExchange.subject || '(Sans sujet)'}</p>
                <p className="text-sm text-blue-400">De: {selectedExchange.from}</p>
              </div>

              {/* Recherche */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Rechercher un client</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={clientSearchTerm}
                    onChange={(e) => setClientSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchClients()}
                    placeholder="Nom, prénom..."
                    className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2"
                  />
                  <button
                    onClick={searchClients}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Résultats */}
              {clientSearchResults.length > 0 && (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  <p className="text-sm text-slate-400">{clientSearchResults.length} résultat(s) :</p>
                  {clientSearchResults.map((client, idx) => (
                    <div
                      key={idx}
                      onClick={() => linkExchangeToClient(client)}
                      className="flex items-center justify-between p-3 bg-slate-800 hover:bg-slate-700 rounded-lg cursor-pointer border border-transparent hover:border-orange-500/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium">{client.prenom} {client.nom}</p>
                          <p className="text-xs text-slate-500">
                            {client.id_qhare && `ID: ${client.id_qhare}`}
                            {client.dpt && ` • Dpt ${client.dpt}`}
                          </p>
                        </div>
                      </div>
                      <button className="px-3 py-1 bg-orange-600 hover:bg-orange-700 rounded text-sm">
                        {linkingExchange === selectedExchange._id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          "Rattacher"
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {clientSearchResults.length === 0 && clientSearchTerm.length >= 2 && (
                <p className="text-center text-slate-500 py-4">Aucun client trouvé</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de rejet */}
      {showRejectModal && selectedExchange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between bg-red-900/20">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                Rejeter l'échange
              </h2>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedExchange(null);
                }}
                className="p-2 hover:bg-slate-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Info échange */}
              <div className="bg-slate-800 rounded-lg p-3">
                <p className="text-sm text-slate-400">Échange à rejeter :</p>
                <p className="font-medium mt-1">{selectedExchange.subject || '(Sans sujet)'}</p>
                <p className="text-sm text-blue-400">De: {selectedExchange.from}</p>
              </div>

              {/* Raison du rejet */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Raison du rejet (optionnel)</label>
                <select
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2"
                >
                  <option value="">Sélectionner une raison...</option>
                  <option value="spam">🚫 Spam / Publicité</option>
                  <option value="doublon">📋 Doublon</option>
                  <option value="hors_sujet">❓ Hors sujet / Non pertinent</option>
                  <option value="test">🧪 Email de test</option>
                  <option value="erreur">⚠️ Erreur d'envoi</option>
                  <option value="autre">📝 Autre</option>
                </select>
              </div>

              {/* Info apprentissage */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <p className="text-xs text-purple-400 flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  SAN-IA apprendra de cette décision pour améliorer le tri automatique.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedExchange(null);
                  }}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  Annuler
                </button>
                <button
                  onClick={rejectExchange}
                  disabled={rejectingExchange === selectedExchange._id}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                  {rejectingExchange === selectedExchange._id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Confirmer le rejet
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détail email */}
      <EmailDetailModal email={selectedEmail} onClose={() => setSelectedEmail(null)} />
    </div>
  );
}
